// Oracle PL/SQL predefined exception helpers (F5.3 Wave-4 R-bucket).
//
// Surfaces the 22 predefined exception names listed in Oracle's PL/SQL
// reference (`DBMS_STANDARD` + system pseudo-exceptions) when the cursor is
// inside an `EXCEPTION` block of a procedural body and the user is typing the
// exception-name slot after `WHEN ` (or `WHEN <prefix>`).
//
// Why a position-aware emit (and not a flat keyword entry):
//   • The list is large (22 entries) and matters only at the `WHEN` slot —
//     adding all of them to `ORACLE_KEYWORD_SET` would pollute every
//     non-EXCEPTION position with low-signal candidates.
//   • The names are conventionally rendered UPPERCASE; bypassing the keyword
//     casing post-process keeps the canonical Oracle form regardless of the
//     user's keyword-case setting (matches how DUAL is emitted as kind=table
//     rather than kind=keyword for the same reason).
//
// Pure module — no Monaco / electron imports. Source-text scan only.

import { tokenize, type Token } from '../context/tokenizer';

const ENGINE = 'oracle' as const;

/**
 * The 22 predefined PL/SQL exception names. Source: Oracle Database PL/SQL
 * Language Reference (`Summary of Predefined PL/SQL Exceptions`) +
 * `OTHERS` catch-all keyword. Order follows the docs page so the resolver's
 * emission ordering is stable and reviewable.
 */
export const ORACLE_PLSQL_EXCEPTION_NAMES: readonly string[] = [
  'NO_DATA_FOUND',
  'TOO_MANY_ROWS',
  'OTHERS',
  'INVALID_CURSOR',
  'INVALID_NUMBER',
  'LOGIN_DENIED',
  'NOT_LOGGED_ON',
  'PROGRAM_ERROR',
  'STORAGE_ERROR',
  'TIMEOUT_ON_RESOURCE',
  'VALUE_ERROR',
  'ZERO_DIVIDE',
  'DUP_VAL_ON_INDEX',
  'CURSOR_ALREADY_OPEN',
  'ACCESS_INTO_NULL',
  'CASE_NOT_FOUND',
  'COLLECTION_IS_NULL',
  'ROWTYPE_MISMATCH',
  'SELF_IS_NULL',
  'SUBSCRIPT_BEYOND_COUNT',
  'SUBSCRIPT_OUTSIDE_LIMIT',
  'SYS_INVALID_ROWID',
];

/**
 * Returns true when the cursor sits at the exception-name slot inside an
 * `EXCEPTION` block — i.e. the most recent significant token before the
 * cursor (skipping any in-progress identifier prefix) is `WHEN`, AND a prior
 * `EXCEPTION` token appears in the statement-scope tokens leading up to the
 * cursor.
 *
 * Conservative implementation: tokenises [statementStart, cursor) and walks
 * backwards from the last token to find a `WHEN` keyword without crossing a
 * `THEN` (which would close the exception-name slot). Then scans forward
 * from `statementStart` to confirm an `EXCEPTION` token appeared before the
 * matched `WHEN`. This avoids false-positives for `CASE WHEN …` (which lacks
 * an EXCEPTION ancestor in the same statement) and for completed handlers
 * `WHEN X THEN <cursor>` (the THEN gate clears the slot).
 */
export function isAtPlsqlExceptionWhenSlot(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  if (cursor <= statementStart) return false;
  const toks = tokenize(source, statementStart, cursor, ENGINE);
  if (toks.length === 0) return false;

  // Walk back from the most recent token. Allow ONE trailing identifier
  // (the in-progress prefix the user is typing, e.g. `NO_D`) before we
  // require a `WHEN`.
  let i = toks.length - 1;
  if (i >= 0) {
    const last = toks[i]!;
    if (last.type === 'ident' || last.type === 'kw') {
      // Could be either the in-progress name OR the `WHEN` itself.
      if (last.text.toUpperCase() === 'WHEN') {
        // Cursor sits immediately after `WHEN ` with no prefix typed.
        return hasPriorExceptionToken(toks, i);
      }
      // Treat as in-progress identifier; back up one token to find WHEN.
      i -= 1;
    }
  }
  if (i < 0) return false;
  const candidate = toks[i]!;
  if (candidate.type !== 'kw' && candidate.type !== 'ident') return false;
  if (candidate.text.toUpperCase() !== 'WHEN') return false;

  // Reject the case where a `THEN` already closed this handler's name slot.
  // Walking forward from `WHEN` to the cursor, a `THEN` between them means
  // the user is past the name slot (in the handler body).
  for (let j = i + 1; j < toks.length; j += 1) {
    const t = toks[j]!;
    if ((t.type === 'kw' || t.type === 'ident') &&
        t.text.toUpperCase() === 'THEN') {
      return false;
    }
  }

  return hasPriorExceptionToken(toks, i);
}

/**
 * Returns true when any `EXCEPTION` keyword token appears strictly before
 * `whenIdx` in the supplied token stream. PL/SQL block `EXCEPTION` markers
 * always introduce the handler section, so any earlier occurrence in the
 * same statement-scope is enough.
 */
function hasPriorExceptionToken(
  toks: readonly Token[],
  whenIdx: number,
): boolean {
  for (let k = 0; k < whenIdx; k += 1) {
    const t = toks[k]!;
    if (t.type !== 'kw' && t.type !== 'ident') continue;
    if (t.text.toUpperCase() === 'EXCEPTION') return true;
  }
  return false;
}
